import type { BackupPayload } from './types'

const ENVELOPE_TYPE = 'appnei-encrypted-backup'
const KDF_NAME = 'PBKDF2-SHA-256'
const ITERATIONS = 250_000

export interface EncryptedBackup {
  formatVersion: 1
  type: typeof ENVELOPE_TYPE
  algorithm: 'AES-GCM'
  kdf: typeof KDF_NAME
  iterations: number
  salt: string
  iv: string
  ciphertext: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toBase64(value: ArrayBuffer | Uint8Array): string {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value)
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000))
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

function subtleCrypto(): SubtleCrypto {
  if (!globalThis.crypto?.subtle) throw new Error('Crittografia non disponibile in questo browser.')
  return globalThis.crypto.subtle
}

function assertPassword(password: string): void {
  if (password.trim().length < 8) throw new Error('La password deve contenere almeno 8 caratteri.')
}

async function deriveKey(password: string, salt: Uint8Array<ArrayBuffer>, usage: KeyUsage, iterations = ITERATIONS): Promise<CryptoKey> {
  const subtle = subtleCrypto()
  const material = await subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey'])
  return subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, material, { name: 'AES-GCM', length: 256 }, false, [usage])
}

export function isEncryptedBackup(value: unknown): value is EncryptedBackup {
  if (!isRecord(value)) return false
  return value.formatVersion === 1 && value.type === ENVELOPE_TYPE && value.algorithm === 'AES-GCM' && value.kdf === KDF_NAME && typeof value.iterations === 'number' && Number.isInteger(value.iterations) && value.iterations >= 100_000 && value.iterations <= 1_000_000 && typeof value.salt === 'string' && typeof value.iv === 'string' && typeof value.ciphertext === 'string'
}

export async function encryptBackup(payload: BackupPayload, password: string): Promise<EncryptedBackup> {
  assertPassword(password)
  const subtle = subtleCrypto()
  const salt = new Uint8Array(new ArrayBuffer(16))
  const iv = new Uint8Array(new ArrayBuffer(12))
  globalThis.crypto.getRandomValues(salt)
  globalThis.crypto.getRandomValues(iv)
  const key = await deriveKey(password, salt, 'encrypt')
  const ciphertext = await subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(payload)))
  return { formatVersion: 1, type: ENVELOPE_TYPE, algorithm: 'AES-GCM', kdf: KDF_NAME, iterations: ITERATIONS, salt: toBase64(salt), iv: toBase64(iv), ciphertext: toBase64(ciphertext) }
}

export async function decryptBackup(value: unknown, password: string): Promise<unknown> {
  if (!isEncryptedBackup(value)) throw new Error('File crittografato non riconosciuto.')
  assertPassword(password)
  try {
    const salt = fromBase64(value.salt)
    const iv = fromBase64(value.iv)
    const key = await deriveKey(password, salt, 'decrypt', value.iterations)
    const plaintext = await subtleCrypto().decrypt({ name: 'AES-GCM', iv }, key, fromBase64(value.ciphertext))
    return JSON.parse(new TextDecoder().decode(plaintext)) as unknown
  } catch {
    throw new Error('Password errata o backup crittografato corrotto.')
  }
}
